'use client';

import { useCallback } from 'react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@ps/ui/atoms/dropdown-menu';
import { cn } from '@ps/ui/utils';

import {
  useDispatchAction,
  useVideoClipsContext,
} from '../../contexts/video-player';
import { type AnyVideo, isPopClipVideo } from '../../functions';

const REPORT_REASONS = [
  { label: 'Inappropriate', value: 'inappropriate' },
  { label: 'Aggressive', value: 'aggressive' },
  { label: 'Spam', value: 'spam' },
];

const DESTRUCTIVE_ITEM = 'text-red-600 focus:text-red-600';

type MoreActionProps = {
  video: AnyVideo;
  currentUserId?: string;
  isRealEstateListing?: boolean;
  onDelete?: (video: AnyVideo) => void;
  className?: string;
};

/**
 * The in-player "…" overflow menu. A right-aligned shadcn dropdown anchored to
 * the trigger (replaces the ported-from-RN bottom sheet). Report expands to a
 * reason sub-menu; picking one dispatches `reportVideo`. Visibility mirrors
 * legacy `useAnyVideoMoreActions`:
 *   - Edit / Delete — owner in creator mode (`editable && isOwner`).
 *   - Share — non-real-estate clips, unless the host opts out via
 *     `canShare === false` (no permalink to build, e.g. the `/videos` library).
 *   - Report — non-owners on non-real-estate clips.
 */
export function MoreAction({
  video,
  currentUserId,
  isRealEstateListing,
  className,
}: MoreActionProps) {
  const { paused$, editable, canShare: hostCanShare } = useVideoClipsContext();
  const dispatch = useDispatchAction();

  const isOwner = currentUserId != null && video.author?.id === currentUserId;
  const isCreatorMode = Boolean(editable) && isOwner;
  // Hide Share when it's not a real clip target, OR when the host can't build a
  // share URL (`canShare === false`) — otherwise the item would no-op. Undefined
  // host value defaults to shareable (existing series/course players).
  const canShare = !isRealEstateListing && hostCanShare !== false;
  // Report is a clip-only action (the report API keys off a clip id); a bare
  // video (e.g. the welcome reel) has nothing to report.
  const canReport = !isOwner && !isRealEstateListing && isPopClipVideo(video);

  const sendAction = useCallback(
    (action: string, extra?: Record<string, unknown>) => {
      // Edit hands off to the owner editor, so pause playback first. Share opens
      // a non-blocking dialog and must NOT stop the video.
      if (action === 'editVideo') {
        paused$.next(true);
      }
      dispatch({ type: action, video, ...extra });
    },
    [video, paused$, dispatch],
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="More options"
          onClick={e => e.stopPropagation()}
          className={cn(
            'bg-surface-2 text-content-1 flex h-[50px] w-[50px] items-center justify-center rounded-full transition-transform hover:scale-110 active:scale-95',
            className,
          )}
        >
          <svg viewBox="0 0 24 24" fill="none" width={24} height={24}>
            <circle cx="4" cy="12" r="2" fill="currentColor" />
            <circle cx="12" cy="12" r="2" fill="currentColor" />
            <circle cx="20" cy="12" r="2" fill="currentColor" />
          </svg>
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="min-w-40">
        {isCreatorMode && (
          <DropdownMenuItem onSelect={() => sendAction('editVideo')}>
            Edit
          </DropdownMenuItem>
        )}
        {canShare && (
          <DropdownMenuItem onSelect={() => sendAction('shareVideo')}>
            Share
          </DropdownMenuItem>
        )}
        {isCreatorMode && (
          <DropdownMenuItem
            className={DESTRUCTIVE_ITEM}
            onSelect={() => sendAction('deleteVideo')}
          >
            Delete
          </DropdownMenuItem>
        )}
        {canReport && (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger className={DESTRUCTIVE_ITEM}>
              Report
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              <DropdownMenuLabel className="text-muted-foreground text-xs font-normal">
                Select a reason
              </DropdownMenuLabel>
              {REPORT_REASONS.map(reason => (
                <DropdownMenuItem
                  key={reason.value}
                  onSelect={() =>
                    sendAction('reportVideo', { reason: reason.value })
                  }
                >
                  {reason.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
